/**
 * `useAgentRegistry` — registry-backed verification-tier resolution for the
 * Live Agent view (ADR-0159 §4).
 *
 * Reads via the existing `AgentAPI` bag (`useOptionalApi()`), so no new
 * HTTP surface is introduced. The bag's `list()` uses the per-page cache
 * inside `createRealAgentsApi` — subsequent renders reuse the cached
 * `Agent[]` without re-fetching.
 *
 * Resolution (§4):
 *   'verified'   — a registry row exists AND `public_keys.length > 0`
 *   'identified' — a registry row exists, `public_keys` is empty
 *                  (Phase-1 posture for every named agent)
 *   'unknown'    — no registry row (or the fetch is in-flight / failed)
 *
 * NEVER defaults to `'verified'`. A failed / pending fetch always resolves
 * to `'unknown'`; the signature preview gate falls back to the "NO KEY"
 * state (§4 "Behavioral consequences", MandateSigningCard §5).
 *
 * When a roster entry maps to multiple origin identities (Claude →
 * ClaudeBot / Claude-User / Claude-SearchBot), the hook resolves to the
 * highest-tier row (verified > identified > unknown). The identity
 * evidence panel still lists all of them side by side — that's a
 * separate concern.
 *
 * Uses `useOptionalApi()` so the view still renders in test contexts
 * without an `<ApiProvider>` — the fallback tier is `'unknown'` (no
 * `verified` overclaim).
 */

import { useEffect, useMemo, useState } from 'react';

import type { Agent, AgentId } from '@sill/types';

import { useOptionalApi } from '../../../api/api-context.js';
import { ORIGIN_AGENT_ID, type VerificationTier } from '../roster.js';

export interface UseAgentRegistryResult {
  /** Resolve the tier for a roster id (`'claude'`, `'chatgpt'`, ...). */
  tierFor: (rosterId: string) => VerificationTier;
  /** True while the initial `AgentAPI.list()` is in-flight. */
  loading: boolean;
  /**
   * True iff the last `AgentAPI.list()` call rejected. Distinct from
   * `loading`: a permanent-failure session isn't in-flight AND has no
   * data — the view surfaces an honest inline note near the picker so
   * the failure doesn't render as silent-verified. Never `true` when
   * `useOptionalApi()` returned `null` (no provider ≠ failed fetch).
   *
   * ADR-0159 MINOR 2 fix (security review): a permanent-failure session
   * without this signal renders tier='unknown' for every roster entry
   * with no user-visible cue that the registry is unreachable.
   */
  failed: boolean;
}

/**
 * Rank tiers so the "highest-tier row" resolution is a straight `Math.max`
 * on the mapped ranks. Kept module-scope so the memoized `tierFor` doesn't
 * re-allocate per render.
 */
const TIER_RANK: Readonly<Record<VerificationTier, number>> = {
  verified: 2,
  identified: 1,
  unknown: 0,
};

const RANK_TO_TIER: readonly VerificationTier[] = [
  'unknown',
  'identified',
  'verified',
];

function tierForAgent(agent: Agent): VerificationTier {
  return agent.public_keys.length > 0 ? 'verified' : 'identified';
}

export function useAgentRegistry(): UseAgentRegistryResult {
  const api = useOptionalApi();
  const [agents, setAgents] = useState<readonly Agent[] | null>(null);
  const [loading, setLoading] = useState<boolean>(api !== null);
  const [failed, setFailed] = useState<boolean>(false);

  useEffect(() => {
    if (api === null) {
      setLoading(false);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    api.agents
      .list()
      .then((items) => {
        if (cancelled) return;
        setAgents(items);
        setLoading(false);
      })
      .catch(() => {
        // Failure = 'unknown' tier for every roster entry AND `failed`
        // flips true so the view surfaces the honest inline note.
        // Never throws; the view stays usable when the API is
        // unreachable (ADR-0159 MINOR 2 fix).
        if (cancelled) return;
        setAgents([]);
        setFailed(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const tierFor = useMemo(() => {
    // Index agents by agent_id for O(1) lookup during resolution.
    const byId = new Map<AgentId, Agent>();
    if (agents !== null) {
      for (const a of agents) {
        byId.set(a.agent_id, a);
      }
    }
    return (rosterId: string): VerificationTier => {
      // In-flight or failed → 'unknown' (never 'verified').
      if (agents === null) return 'unknown';
      const originIds = ORIGIN_AGENT_ID[rosterId];
      if (originIds === undefined || originIds.length === 0) return 'unknown';
      let bestRank = TIER_RANK.unknown;
      for (const originId of originIds) {
        const row = byId.get(originId);
        if (row === undefined) continue;
        const rank = TIER_RANK[tierForAgent(row)];
        if (rank > bestRank) bestRank = rank;
      }
      return RANK_TO_TIER[bestRank] ?? 'unknown';
    };
  }, [agents]);

  return { tierFor, loading, failed };
}
